import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import Banner from "../components/Banner";
import { ArrowRight, MessageSquare, Users, TrendingUp, Sparkles, Cpu, Gamepad2, Trophy, UtensilsCrossed } from "lucide-react";
import { Button } from "../components/ui/button";

const ICONS = { MessageSquare, Cpu, Gamepad2, Trophy, UtensilsCrossed, Sparkles };

export default function Home() {
  const { user } = useAuth();
  const [home, setHome] = useState(null);
  const [cats, setCats] = useState([]);
  const [threads, setThreads] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/homepage").then((r) => r.data),
      api.get("/categories").then((r) => r.data),
      api.get("/threads?limit=6").then((r) => r.data),
    ]).then(([h, c, t]) => {
      setHome(h); setCats(c); setThreads(t);
    });
  }, []);

  if (!home) return <div className="p-8" data-testid="home-loading">Loading...</div>;

  return (
    <div className="pastel-grain min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {home.announcement && (
          <div data-testid="home-announcement"
            className="brutal-border brutal-shadow-sm rounded-xl px-4 py-3 mb-6 font-medium fade-up"
            style={{ background: "var(--green)" }}>
            <span className="font-bold">Announcement: </span>{home.announcement}
          </div>
        )}

        <div className="mb-6"><Banner position="top" /></div>

        <section className="grid lg:grid-cols-12 gap-6 mb-12 fade-up">
          <div className="lg:col-span-8 brutal-border rounded-2xl p-8 sm:p-12 brutal-shadow"
            style={{ background: "var(--primary)" }}>
            <div className="sticker mb-6 inline-flex" style={{ background: "white" }}>
              <Sparkles size={14} strokeWidth={2.5} /> Forum for the curious
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[0.95]">
              {home.hero_title}
            </h1>
            <p className="mt-5 text-lg sm:text-xl leading-relaxed max-w-2xl font-medium text-zinc-800">
              {home.hero_subtitle}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to={user ? "#categories" : "/register"} data-testid="home-hero-cta">
                <Button className="h-12 px-6 brutal-border brutal-shadow rounded-xl font-bold text-base gap-2"
                  style={{ background: "var(--ink)", color: "white" }}>
                  {home.hero_cta} <ArrowRight size={18} />
                </Button>
              </Link>
              <Link to="/membership">
                <Button className="h-12 px-6 brutal-border brutal-shadow rounded-xl font-bold text-base bg-white hover:bg-white text-[var(--ink)]">
                  View memberships
                </Button>
              </Link>
            </div>
          </div>

          <div className="lg:col-span-4 grid grid-cols-2 lg:grid-cols-1 gap-4">
            <StatCard icon={<MessageSquare size={20} />} label="Threads" value={threads.length > 0 ? "Active" : "New"} color="var(--secondary)" />
            <StatCard icon={<Users size={20} />} label="Members" value="Growing" color="var(--accent)" />
            <StatCard icon={<TrendingUp size={20} />} label="Engagement" value="High" color="var(--pink)" />
            <StatCard icon={<Sparkles size={20} />} label="Categories" value={cats.length} color="var(--green)" />
          </div>
        </section>

        <section id="categories" className="mb-12">
          <div className="flex items-end justify-between mb-6">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-500">Explore</div>
              <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">
                Categories
              </h2>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {cats.map((c, i) => {
              const Icon = ICONS[c.icon] || MessageSquare;
              return (
                <Link key={c.id} to={`/category/${c.id}`} data-testid={`category-card-${c.id}`}
                  className="brutal-border brutal-shadow rounded-2xl p-6 fade-up brutal-hoverable"
                  style={{ background: c.color || "var(--surface)", animationDelay: `${i * 50}ms` }}>
                  <div className="w-12 h-12 brutal-border rounded-xl grid place-items-center mb-4 bg-white">
                    <Icon size={22} strokeWidth={2.5} />
                  </div>
                  <h3 className="font-display font-black text-xl">{c.name}</h3>
                  <p className="text-sm font-medium text-zinc-700 mt-1 line-clamp-2">{c.description}</p>
                  <div className="mt-4 text-xs font-bold uppercase tracking-widest">
                    {c.thread_count} threads →
                  </div>
                </Link>
              );
            })}
          </div>
        </section>

        <section>
          <div className="flex items-end justify-between mb-6">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-500">Latest</div>
              <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">
                Recent threads
              </h2>
            </div>
          </div>
          <div className="grid gap-4">
            {threads.length === 0 ? (
              <div className="brutal-border brutal-shadow-sm rounded-2xl p-8 text-center bg-white">
                <p className="text-zinc-600 font-medium">No threads yet. Be the first!</p>
              </div>
            ) : threads.map((t, i) => (
              <Link key={t.id} to={`/thread/${t.id}`} data-testid={`thread-card-${t.id}`}
                className="brutal-border brutal-shadow-sm rounded-xl p-5 bg-white brutal-hoverable fade-up"
                style={{ animationDelay: `${i * 30}ms` }}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="sticker text-[10px]" style={{ background: "var(--secondary)" }}>
                      {t.category_name}
                    </div>
                    <h3 className="font-display font-bold text-lg mt-2">{t.title}</h3>
                    <p className="text-sm text-zinc-600 mt-1 line-clamp-2 font-medium">{t.content}</p>
                    <div className="mt-3 text-xs text-zinc-500 font-medium">
                      by <span className="font-bold text-zinc-700">{t.author_name}</span> · {t.reply_count} replies · ↑ {t.votes}
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, color }) {
  return (
    <div className="brutal-border brutal-shadow-sm rounded-xl p-4 flex items-center gap-3" style={{ background: color }}>
      <div className="w-10 h-10 grid place-items-center brutal-border rounded-lg bg-white">{icon}</div>
      <div>
        <div className="text-xs uppercase font-bold tracking-widest text-zinc-700">{label}</div>
        <div className="font-display font-black text-xl leading-none mt-1">{value}</div>
      </div>
    </div>
  );
}
